import { useEffect, useRef, useState } from "react";
import app from "../components/app";

// Base CSS bundles always injected into the preview iframe. `?version=<ts>`
// is a cache-buster that changes each time the module loads, so a hard reload
// picks up any updated bundle without stale-cache pain.
const PREVIEW_VERSION = typeof window !== "undefined" ? Date.now() : "";
const PREVIEW_CSS_URLS = [
    "https://media.mediadirhub.com/minify/css/bootstrap-5.3.8.min.css",
    "https://media.mediadirhub.com/minify/css/home.css",
];

// One Ace instance shared across the three tabs — we swap its content and
// syntax mode when the user switches tabs.
let editor = null;

const ACE_MODE = { html: "ace/mode/html", css: "ace/mode/css", js: "ace/mode/javascript" };

// The HTML tab holds a *fragment*, not a document — composePreview wraps it for
// the preview, and the published page injects it into an existing body. Markup
// that only makes sense in a whole document, or that can reach outside the
// fragment, is taken out on the way through.
//
// All of the policy lives in two arrays, each with one function that applies it:
//
//     REMOVE_TAGS  → tagsRemove(html)
//     REMOVE_ATTRS → attrRemove(html)
//
// sanitizeHtml() passes the html through both and reports what it took out. To
// change what is allowed, add or delete a row — nothing else needs touching.
//
// IMPORTANT: this is not a security boundary. It is regex over markup, running
// in the author's own browser, and everything here is bypassed by POSTing to
// saveHtmlData directly. Its job is to stop authors breaking their own pages by
// accident. Keeping malicious markup off published sites has to happen
// server-side, on save/publish.

// --- Tags -------------------------------------------------------------------
// name    — tag name this row covers, matched case-insensitively.
// content — true when the element's *contents* go with its tags. Without it,
//           dropping <script> would leave the JavaScript behind as visible page
//           text. Left off for wrappers like <html>/<body>, whose contents are
//           the page itself.
// unless  — skip the row entirely when this (non-global) regex matches.
// label   — what the toast calls it.
// re      — escape hatch for a row that isn't a named tag (<!DOCTYPE>).
//
// Deliberately NOT here: <iframe>, <svg>, <form>, <style>, <img>, <video>. Each
// has a common legitimate use (video/map embeds, icons, contact forms, scoped
// CSS), so removing them would cost far more than it saves.
const REMOVE_TAGS = [
    // The JS tab is where script belongs, and composePreview already wraps that
    // buffer in a <script> of its own. Escaped text (&lt;script&gt;) is left
    // alone — that renders as characters, not an element.
    { label: "<script>",   name: "script",   content: true },

    // Document shell. Pasting a whole page brings these along; inside a
    // fragment they are invalid and the browser discards or relocates them
    // unpredictably. Unwrapped rather than deleted with their contents — so a
    // <style> inside <head> survives into the fragment (where it still applies)
    // instead of being silently thrown away, while <head>'s other children are
    // each handled by their own row below.
    { label: "<!DOCTYPE>", re: /<!DOCTYPE[^>]*>/gi },
    { label: "<html>",     name: "html" },
    { label: "<head>",     name: "head" },
    { label: "<body>",     name: "body" },

    // Rewrites every relative URL on the page — hijacks all links and assets.
    { label: "<base>",     name: "base" },
    // http-equiv="refresh" redirects the visitor straight off the page. The name
    // edge in the compiled regex keeps <metadata> (valid inside SVG) out of it.
    { label: "<meta>",     name: "meta" },
    // External stylesheet / preload — breaks global layout, leaks referrer.
    { label: "<link>",     name: "link" },
    // Replaces the entire page body.
    { label: "<frameset>", name: "frameset", content: true },
    { label: "<noframes>", name: "noframes", content: true },
    { label: "<frame>",    name: "frame" },
    // Plugin / exec surface — loads arbitrary embedded content.
    { label: "<object>",   name: "object",   content: true },
    { label: "<applet>",   name: "applet",   content: true },
    { label: "<embed>",    name: "embed" },
    // Experimental iframe-alike.
    { label: "<portal>",   name: "portal" },

    // <title> belongs to the document — but it is also how inline SVG supplies
    // its accessible name. Rather than parse, skip the row when the fragment has
    // an <svg> in it: stripping every icon's label would be worse than leaving a
    // stray page title behind.
    { label: "<title>",    name: "title",    content: true, unless: /<\s*svg\b/i },
];

// Compile each row's regexes once, at module load.
//   content: true  → the matching pair and everything between it, then any
//                    unpaired leftovers (nesting makes the non-greedy pair match
//                    stop at the inner closer, and pasted markup can carry an
//                    opener or closer with no partner at all).
//   content: false → the tags only; contents stay where they are.
//
// `\s*` after `<` catches `< script`. The (?![\w-]) after the name is what stops
// a row from swallowing a longer name: `\b` would not, because it also matches
// before a hyphen, and every custom element has one — so `title` would eat
// <title-bar> and `link` would eat <link-preview>. Blocking a following letter
// keeps <metadata> and <scriptish> out too.
//
// `[^>]*` stops at the first ">", so a ">" inside an attribute value ends the
// match early — accepted, see the note above about this not being a security
// boundary. Names are plain letters, so none need escaping.
const TAG_RULES = REMOVE_TAGS.map(function(row){
    if (row.re) return { label: row.label, unless: row.unless, res: [row.re] };
    const edge = "(?![\\w-])";
    const res = [];
    if (row.content) {
        res.push(new RegExp("<\\s*" + row.name + edge + "[^>]*>[\\s\\S]*?<\\s*\\/\\s*" + row.name + edge + "\\s*>", "gi"));
    }
    res.push(new RegExp("<\\s*\\/?\\s*" + row.name + edge + "[^>]*>", "gi"));
    return { label: row.label, unless: row.unless, res: res };
});

// Remove every REMOVE_TAGS row from an HTML fragment.
// Returns { html, removed: ["<script>", ...] } — `removed` drives the toast.
//
// Compares before/after instead of calling re.test() first: these are shared
// module-level /g regexes, and test() advances lastIndex on a match, so a
// test-then-replace pair would go stale on the next call. (`unless` is
// deliberately non-global, so testing that one is safe.)
function tagsRemove(raw) {
    let html = typeof raw === "string" ? raw : "";
    const removed = [];
    TAG_RULES.forEach(function(rule){
        if (rule.unless && rule.unless.test(html)) return;
        rule.res.forEach(function(re){
            const next = html.replace(re, "");
            if (next === html) return;
            if (removed.indexOf(rule.label) === -1) removed.push(rule.label);
            html = next;
        });
    });
    return { html: html.trim(), removed: removed };
}

// --- Attributes -------------------------------------------------------------
// name  — attribute names this row covers (anchored, case-insensitive).
// value — optional: only remove when the value matches too, so href="/ok"
//         survives while href="javascript:…" does not.
// label — what the toast calls it.
const REMOVE_ATTRS = [
    // The XSS vector that needs no <script> at all: <img src=x onerror="…">.
    // Anchoring is what keeps `data-on-click=` out of it, and {2,} letters stop
    // a bare `on=` from matching.
    { label: "inline event handlers (onclick, onerror, …)", name: /^on[a-z]{2,}$/i },
    // <a href="javascript:…">, and the same trick on the other URL attributes.
    { label: "javascript: URLs", name: /^(?:href|src|action|formaction|xlink:href)$/i, value: /^javascript:/i },
    // <iframe> is allowed (video/map embeds), and srcdoc is the hole that lets
    // one carry a whole document — scripts included — past the <script> row.
    { label: "srcdoc", name: /^srcdoc$/i },
];

// Only *start* tags are walked: requiring a letter straight after "<" skips
// closing tags, comments and doctypes, along with body text that merely looks
// like markup. Quoted runs are matched whole so a ">" inside an attribute value
// doesn't end the tag early.
const START_TAG_RE = /<([a-zA-Z][^\s/>]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;

// One attribute of a start tag: leading whitespace, name, optional value. The
// whitespace is part of the match so removing an attribute doesn't leave a
// double space behind.
const ATTR_RE = /\s*([^\s"'=<>/]+)(\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?/g;

// Unwrap an attribute value for testing against a row's `value` regex.
function attrValue(assign) {
    let value = assign.replace(/^\s*=\s*/, "");
    if (/^["']/.test(value)) value = value.slice(1, -1);
    // Browsers strip tab/newline/CR inside URL attributes before acting on them,
    // so "java\nscript:alert(1)" still runs. Do the same before testing.
    return value.replace(/[\t\n\r\f]/g, "").trim();
}

// Remove every REMOVE_ATTRS row from the start tags of an HTML fragment.
// Returns { html, removed: [...] }.
//
// Works tag by tag rather than over the whole string: a global search for
// ` on…=` would also hit body text, and values that merely contain something
// matching (`href="/x?onclick=1"`).
function attrRemove(raw) {
    const source = typeof raw === "string" ? raw : "";
    const removed = [];
    const html = source.replace(START_TAG_RE, function(tag, name, attrs){
        if (!attrs) return tag;
        let changed = false;
        const kept = attrs.replace(ATTR_RE, function(pair, attrName, assign){
            const value = assign ? attrValue(assign) : "";
            let drop = null;
            REMOVE_ATTRS.forEach(function(rule){
                if (drop || !rule.name.test(attrName)) return;
                if (rule.value && !rule.value.test(value)) return;
                drop = rule;
            });
            if (!drop) return pair;
            if (removed.indexOf(drop.label) === -1) removed.push(drop.label);
            changed = true;
            return "";
        });
        // Trailing whitespace inside a start tag is insignificant, so drop what
        // a removal left behind; a self-closing "/" is not whitespace and stays.
        return changed ? "<" + name + kept.replace(/\s+$/, "") + ">" : tag;
    });
    return { html: html, removed: removed };
}

// --- Applying both ----------------------------------------------------------

// Pass an HTML fragment through both filters. Tags go first, so an attribute
// that only existed on a tag being removed isn't reported on its own as well.
// Returns { html, removed } carrying the labels from both.
function sanitizeHtml(raw) {
    const tags  = tagsRemove(raw);
    const attrs = attrRemove(tags.html);
    return { html: attrs.html.trim(), removed: tags.removed.concat(attrs.removed) };
}

// Escape for showAlert, which assigns the message via innerHTML — an unescaped
// "<script>" would be parsed into an element and render as nothing visible.
function escapeForToast(text) {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Sanitize a fragment and describe what changed in one ready-to-toast string
// ("" when nothing was removed). Returns { html, removed, message }.
//
// One message, not one per problem: .custom-alert is position:fixed at a single
// spot with no stacking container, so two simultaneous toasts would land exactly
// on top of each other.
function reviewHtml(raw) {
    const clean = sanitizeHtml(raw);
    const parts = [];
    if (clean.removed.indexOf("<script>") !== -1) {
        parts.push("&lt;script&gt; is not allowed in page HTML — put your code in the JS tab.");
    }
    const others = clean.removed.filter(function(label){ return label !== "<script>"; });
    if (others.length) {
        // Cap the list so a badly-pasted page doesn't produce an essay.
        const shown = others.slice(0, 4).map(escapeForToast).join(", ");
        const rest  = others.length > 4 ? " and " + (others.length - 4) + " more" : "";
        parts.push("Removed from your HTML: " + shown + rest + ".");
    }
    return { html: clean.html, removed: clean.removed, message: parts.join(" ") };
}

export default function HtmlEditor() {
    const iframeRef = useRef(null);
    const [sources, setSources] = useState({ html: "", css: "", js: "" });
    const [tab, setTab] = useState("html");         // "html" | "css" | "js"
    const [preview, setPreview] = useState(false);  // true = show preview iframe
    const [device, setDevice] = useState("desktop"); // "desktop" | "mobile" — preview width only
    // The JS tab is owners-only. Read once per mount: it depends on the stored
    // login, which does not change while the editor is open.
    const showJsTab = app.isDonaldApp();
    // Never leave the editor sitting on a tab that is not rendered — the pill
    // would show nothing selected and the pane would be editing an invisible
    // buffer.
    useEffect(() => {
        if (!showJsTab && tab === "js") setTab("html");
    }, [showJsTab, tab]);
    const [loading, setLoading] = useState(true);   // true until getHtmlData resolves
    // Save/publish in flight — swaps the Save button for a spinner. Cleared by
    // the host page calling window.htmlPublishEvent(), or by the safety
    // timeout in onSave if it never does.
    const [isSaving, setIsSaving] = useState(false);
    const savingTimerRef = useRef(null);
    const sourcesRef = useRef(sources);
    sourcesRef.current = sources;
    // Keep a ref of the current tab so the Ace change-handler (whose closure
    // is fixed at mount time via useEffect([]) below) can always read the
    // active tab. Without this, typing on the CSS/JS tab would write into
    // sources.html.
    const tabRef = useRef(tab);
    tabRef.current = tab;
    // Ignore the change event fired while we're loading a tab's content into
    // Ace via editor.setValue() — we don't want that programmatic write to
    // clobber sources with an intermediate state.
    const suppressChangeRef = useRef(false);
    // True once getHtmlData has resolved (any path). Ace's boot callback
    // reads this to decide whether to bind sources into the editor — if the
    // server data has already landed by the time Ace mounts, we still need
    // to populate it.
    const dataReadyRef = useRef(false);
    // Last message we toasted for the HTML buffer, or "" while it is clean.
    // Typing fires a change event per keystroke, so without this the user would
    // get a fresh toast on every character. Keyed on the message rather than a
    // bare boolean so that a *different* problem appearing still warns, and so
    // clearing one problem while another remains doesn't re-toast.
    const lastHtmlWarningRef = useRef("");

    // Toast once per distinct problem in the HTML buffer. Returns the review so
    // callers can use the sanitized html. Only reads/writes refs and calls app,
    // so the mount-time closures in the useEffect([])s below can hold onto the
    // first instance safely.
    const reviewAndWarn = function(html) {
        const review = reviewHtml(html);
        if (review.message !== lastHtmlWarningRef.current) {
            lastHtmlWarningRef.current = review.message;
            if (review.message) app.showAlert("danger", review.message);
        }
        return review;
    };

    // Tag #ai-editor with a scope class while this component is mounted.
    // Skip on localhost so dev sessions don't inherit prod-only styling.
    useEffect(() => {
        if (app.isLocalhost()) return;
        const root = document.getElementById("ai-editor");
        if (root) root.classList.add("htmlcode-editor");
        return function(){
            const el = document.getElementById("ai-editor");
            if (el) el.classList.remove("htmlcode-editor");
        };
    }, []);

    // Boot Ace once. The editor is mounted into #html-editor-pane and its
    // content/mode is driven imperatively (see the tab-switch effect).
    // NOTE: we intentionally do NOT setValue at boot — the editor stays empty
    // until getHtmlData resolves and pushes the server content in.
    useEffect(() => {
        app.loadScript("https://cdnjs.cloudflare.com/ajax/libs/ace/1.31.1/ace.min.js", "ace").then(function(){
            editor = window.ace.edit("html-editor-pane", {
                mode: ACE_MODE[tab] || ACE_MODE.html,
                selectionStyle: "text",
                showPrintMargin: false,
                theme: "ace/theme/chrome",
                wrap: true,
                fontSize: 13,
                tabSize: 2,
                useSoftTabs: true,
            });
            // If getHtmlData already resolved before Ace booted, bind now.
            if (dataReadyRef.current) {
                suppressChangeRef.current = true;
                editor.setValue(sourcesRef.current[tabRef.current] || "", -1);
                suppressChangeRef.current = false;
            }
            editor.on("change", function(){
                if (suppressChangeRef.current) return;    // ignore programmatic writes
                const value = editor.getValue();
                const currentTab = tabRef.current;
                // Covers typing and paste alike — Ace fires change for both.
                // Warn only here; the buffer is left exactly as typed so the
                // cursor never jumps mid-edit. The removal itself happens on
                // load, on save and in the preview (see below).
                if (currentTab === "html") reviewAndWarn(value);
                setSources(prev => {
                    if (prev[currentTab] === value) return prev;
                    return { ...prev, [currentTab]: value };
                });
                if (autoSaveRef.current) autoSaveRef.current();
            });
        });

        return function cleanup(){
            if (editor) {
                try { editor.destroy(); } catch (_) {}
                editor = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the saved page from the backend. Server is the source of truth —
    // no localStorage fallback. Shape: { html: "...", data: '{"css":"...","js":"..."}' }.
    useEffect(function(){
        app.fetchPost({
            method: "getHtmlData",
            appId:  localStorage.getItem("aiAppId"),
            pageId: localStorage.getItem("aiPageId"),
        }).then(function(response){
            if (!response) { dataReadyRef.current = true; setLoading(false); return; }

            let html = typeof response.html === "string" ? response.html : "";
            let css = "";
            let js  = "";
            if (typeof response.data === "string") {
                try {
                    const d = JSON.parse(response.data);
                    if (d && typeof d.css === "string") css = d.css;
                    if (d && typeof d.js  === "string") js  = d.js;
                } catch (_) {}
            } else if (response.data && typeof response.data === "object") {
                if (typeof response.data.css === "string") css = response.data.css;
                if (typeof response.data.js  === "string") js  = response.data.js;
            }

            // Nothing saved yet — leave the buffers empty.
            if (!html && !css && !js) { dataReadyRef.current = true; setLoading(false); return; }

            // Saved content predates these rules, so clean it on open rather than
            // waiting for the user to type. review.html is what gets bound to the
            // buffer, so disallowed markup is gone before it is ever shown — and
            // the toast says what went, since the user did not do it just now.
            const review = reviewAndWarn(html);

            const next = { html: review.html, css: css, js: js };
            setSources(next);

            // If Ace is already mounted, push the fresh content into the
            // active tab. If it isn't, its boot callback reads sourcesRef and
            // picks up the new value on its own.
            if (editor) {
                suppressChangeRef.current = true;
                editor.setValue(next[tabRef.current] || "", -1);
                suppressChangeRef.current = false;
            }
            dataReadyRef.current = true;
            setLoading(false);
        }).catch(function(err){
            console.error("getHtmlData failed", err);
            dataReadyRef.current = true;
            setLoading(false);
        });
    }, []);

    // Swap Ace's mode + content when the user switches HTML/CSS/JS tabs.
    // suppressChangeRef guards the change handler so this programmatic swap
    // doesn't write the CSS content into sources.html (or vice versa).
    useEffect(() => {
        if (!editor || preview) return;
        try {
            editor.session.setMode(ACE_MODE[tab] || ACE_MODE.html);
            const current = editor.getValue();
            if (current !== sources[tab]) {
                suppressChangeRef.current = true;
                editor.setValue(sources[tab] || "", -1);
                suppressChangeRef.current = false;
            }
            setTimeout(function(){ try { editor.resize(true); editor.focus(); } catch (_) {} }, 0);
        } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, preview]);

    // Push composed document into the preview iframe whenever preview is on
    // or the sources change while preview is showing.
    useEffect(() => {
        if (!preview) return;
        const frame = iframeRef.current;
        if (!frame) return;
        frame.srcdoc = composePreview(sources);
    }, [preview, sources]);

    // Central save function — called from every mutation path (editor change,
    // undo, redo, manual Save). Debounced so a burst of keystrokes fires one
    // network round-trip after the user pauses.
    const autoSaveTimer = useRef(null);
    const autoSaveRef = useRef(null);
    const autoSave = function() {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(function(){
            const s = sourcesRef.current;
            // Sanitize on the way out so nothing disallowed is ever persisted or
            // published. Done here rather than in the change handler so the
            // editor buffer is not rewritten under the user's cursor while they
            // type — they have already been toasted about whatever goes.
            const html = sanitizeHtml(s.html).html;
            app.fetchPost({
                method:  "saveHtmlData",
                appId:   localStorage.getItem("aiAppId"),
                pageId:  localStorage.getItem("aiPageId"),
                html:    html,
                data:    JSON.stringify({
                    css: s.css || "",
                    js:  s.js  || "",
                }),
            }).catch(function(err){
                console.error("saveHtmlData failed", err);
            });
        }, 500);
    };

    // Keep a live handle so the Ace change-handler (whose closure was locked
    // at mount time via useEffect([])) always calls the latest autoSave.
    autoSaveRef.current = autoSave;

    const onUndo = function() { if (editor) editor.undo(); autoSave(); };
    const onRedo = function() { if (editor) editor.redo(); autoSave(); };
    // The host page reports a finished publish by calling this — receiving it is
    // the only thing that hides the spinner. Exposed on window because the host
    // is a separate bundle with no import path in here, the same reach-across as
    // the aiOpenAgent button. Registered once and torn down on unmount so a
    // stale closure cannot outlive the editor.
    useEffect(() => {
        window.htmlPublishEvent = function() {
            clearTimeout(savingTimerRef.current);
            setIsSaving(false);
        };
        return function() {
            clearTimeout(savingTimerRef.current);
            if (window.htmlPublishEvent) delete window.htmlPublishEvent;
        };
    }, []);

    const onSave = function() {
        if (isSaving) return;              // ignore repeat clicks while in flight
        setIsSaving(true);
        autoSave();
        document.querySelector(".btn.btn-success.publish-btn")?.click();
        // Never let the spinner hang forever if the host never calls
        // htmlPublishEvent — the same failure mode as the stuck preloader fixed
        // in AI-BUILDER-9B, where a missing completion path left the UI wedged
        // with no way forward.
        clearTimeout(savingTimerRef.current);
        savingTimerRef.current = setTimeout(function(){ setIsSaving(false); }, 15000);
    };
    const onTogglePreview = function() { setPreview(v => !v); };

    return <div id="html-editor-area" className="htmled-shell w-100 h-100 overflow-hidden">
        {/* Toolbar */}
        <div className="htmled-toolbar">
            {/* Left: Undo / Redo — editor-only actions, meaningless once the
                buffer is no longer on screen. */}
            {!preview && <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                <button className="htmled-icon-btn" onClick={onUndo} title="Undo (Cmd/Ctrl+Z)" aria-label="Undo">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 14 4 9l5-5"/>
                        <path d="M4 9h11a5 5 0 0 1 0 10h-4"/>
                    </svg>
                </button>
                <button className="htmled-icon-btn" onClick={onRedo} title="Redo (Cmd/Ctrl+Shift+Z)" aria-label="Redo">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m15 14 5-5-5-5"/>
                        <path d="M20 9H9a5 5 0 0 0 0 10h4"/>
                    </svg>
                </button>
            </div>}

            {/* Center: HTML / CSS / JS tabs while editing, Desktop / Mobile while
                previewing — the tabs edit buffers that are not visible in preview,
                so swap them for the one choice that still applies. */}
            {!preview ? <div className="htmled-tab-pill">
                <button
                    className={`htmled-tab ${tab === "html" ? "active" : ""}`}
                    onClick={() => { setTab("html"); }}
                    aria-pressed={tab === "html"}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="4 17 10 11 4 5"/>
                        <line x1="12" y1="19" x2="20" y2="19"/>
                    </svg>
                    HTML
                </button>
                <button
                    className={`htmled-tab ${tab === "css" ? "active" : ""}`}
                    onClick={() => { setTab("css"); }}
                    aria-pressed={tab === "css"}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16v16H4z"/>
                        <path d="M4 10h16"/>
                        <circle cx="8" cy="14" r="1"/>
                        <line x1="11" y1="14" x2="18" y2="14"/>
                    </svg>
                    CSS
                </button>
                {/* Owners only — see app.isDonaldApp. The JS buffer still loads,
                    saves and runs in the preview for everyone; this only hides
                    the tab that edits it. */}
                {showJsTab && <button
                    className={`htmled-tab ${tab === "js" ? "active" : ""}`}
                    onClick={() => { setTab("js"); }}
                    aria-pressed={tab === "js"}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <path d="M9 8v6a2 2 0 0 1-2 2"/>
                        <path d="M15 8v5a2 2 0 0 0 2 2h1"/>
                    </svg>
                    JS
                </button>}
            </div> : <div className="htmled-tab-pill">
                <button
                    className={`htmled-tab htmled-tab-icon ${device === "desktop" ? "active" : ""}`}
                    onClick={() => setDevice("desktop")}
                    aria-pressed={device === "desktop"}
                    title="Desktop width"
                    aria-label="Desktop width"
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="4" width="20" height="13" rx="1.5"/>
                        <line x1="8" y1="20" x2="16" y2="20"/>
                        <line x1="12" y1="17" x2="12" y2="20"/>
                    </svg>
                </button>
                <button
                    className={`htmled-tab htmled-tab-icon ${device === "mobile" ? "active" : ""}`}
                    onClick={() => setDevice("mobile")}
                    aria-pressed={device === "mobile"}
                    title="Mobile width (380px)"
                    aria-label="Mobile width (380px)"
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="7" y="2" width="10" height="20" rx="2"/>
                        <line x1="10.5" y1="18.5" x2="13.5" y2="18.5"/>
                    </svg>
                </button>
            </div>}

            {/* Right: AI Assistant + Preview + Save */}
            <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                {/* aiOpenAgent is expected to be provided by the host page, same as
                    the publish-btn lookup in onSave — guarded so this button is a
                    no-op rather than a ReferenceError if it isn't defined yet. */}
                <button
                    className="ai-agent-header-btn hide-reseller"
                    onClick={() => { if (typeof window.aiOpenAgent === "function") window.aiOpenAgent(); }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/>
                    </svg>
                    AI Assistant
                </button>
                <button
                    className={`htmled-preview-btn ${preview ? "active" : ""}`}
                    onClick={onTogglePreview}
                    title={preview ? "Back to editor" : "Show preview"}
                >
                    {preview ? "Editor" : "Preview"}
                </button>
                {isSaving
                    ? <div className="save-button-loading">
                        <div className="save-button-loading-spinner"></div>
                      </div>
                    : <button className="htmled-save-btn" onClick={onSave}>
                        Save
                      </button>}
            </div>
        </div>

        {/* Body */}
        <div
            className={`htmled-body ${preview && device === "mobile" ? "htmled-body-mobile" : ""}`}
            style={{position: "relative"}}
        >
            <div
                id="html-editor-pane"
                className="htmled-body-pane"
                style={{display: preview ? "none" : "block"}}
            />
            {/* The sink/card look (inset margin, corners, shadow) lives on this
                wrapper — sizing an iframe directly is the more fragile way to do
                this. The iframe just fills it at 100%, same in both devices. */}
            <div
                className={`htmled-preview-frame ${device === "mobile" ? "htmled-preview-frame-mobile" : ""}`}
                style={{display: preview ? "block" : "none"}}
            >
                <iframe
                    ref={iframeRef}
                    title="html-editor-preview"
                    className="htmled-body-preview"
                    sandbox="allow-scripts allow-same-origin"
                />
            </div>
            {loading && (
                <div className="htmled-loader" style={{
                    position: "absolute", inset: 0, background: "rgba(255,255,255,0.85)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    zIndex: 10, gap: "10px", color: "#4b5563", fontSize: "13px",
                }}>
                    <span style={{
                        width: "18px", height: "18px", border: "2px solid #d1d5db",
                        borderTopColor: "#3899ED", borderRadius: "50%",
                        display: "inline-block", animation: "htmled-spin 0.8s linear infinite",
                    }} />
                    Loading…
                    <style>{"@keyframes htmled-spin { to { transform: rotate(360deg); } }"}</style>
                </div>
            )}
        </div>
    </div>;
}

// Merge the HTML/CSS/JS buffers into a full document for the preview iframe.
function composePreview(s) {
    // Sanitize first so the preview shows what will actually be saved and
    // published, not the raw buffer. This also means the html can never carry its
    // own </head> or </body>, so the old "inject into the user's own document"
    // branch that used to live here was unreachable and has been dropped — there
    // is exactly one wrapping now.
    const html = sanitizeHtml(s.html).html;
    const cssLinks = PREVIEW_CSS_URLS
        .map(url => `<link rel="stylesheet" href="${url}?version=${PREVIEW_VERSION}">`)
        .join("\n    ");
    const styleTag  = s.css ? `<style>${s.css}</style>` : "";
    const scriptTag = s.js  ? `<script>${s.js}<\/script>` : "";
    // home.css ships `html,body{width:100%;height:100%;overflow:hidden}`, which
    // pins the preview document to the iframe viewport and clips anything taller
    // — the preview simply would not scroll. Undo it: let the body grow with its
    // content so the iframe scrolls normally, keeping min-height so a full-height
    // background still fills an empty page. Declared after the bundles (wins on
    // order at equal specificity) but before the author's own <style>, so their
    // CSS can still override it.
    const scrollFix = `<style>html,body{height:auto;min-height:100%;overflow:visible;}</style>`;
    // Every link stays visually and interactively "live" in the preview — cursor,
    // hover states, and any click handler the author attached all still fire —
    // but none of them navigate. Without this, clicking any <a href> replaces the
    // whole composed preview with whatever page it points to; the iframe has no
    // sandbox token that stops a frame navigating itself (only top-navigation is
    // sandboxed), so nothing else here already prevents it.
    // Capturing phase so this runs before any bubble-phase handler the author
    // attached on the link itself, and so it can't be skipped by the author's
    // own stopPropagation().
    const linkGuard = `<script>document.addEventListener("click", function(e){
      var a = e.target.closest && e.target.closest("a[href]");
      if (a) e.preventDefault();
    }, true);<\/script>`;
    const head = `${cssLinks}\n    ${scrollFix}\n    ${linkGuard}\n    ${styleTag}`;

    return `<!DOCTYPE html><html><head>${head}</head><body>${html}${scriptTag}</body></html>`;
}
